import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { CheckIcon, GiftIcon, PlusCircle, Lock, X } from 'lucide-react';
// ─── Exit Upsell Popup ────────────────────────────────────────────────────────
interface UpsellPopupProps {
  onAccept: () => void;
  onDecline: () => void;
}
function UpsellPopup({
  onAccept,
  onDecline
}: UpsellPopupProps) {
  const BUNDLE_PRICE = '17.07';
  const TRUE_TOTAL = '24.39';
  return <motion.div initial={{
    opacity: 0
  }} animate={{
    opacity: 1
  }} exit={{
    opacity: 0
  }} transition={{
    duration: 0.2
  }} className="fixed inset-0 z-50 flex items-end sm:items-center justify-center px-3 sm:px-4 pb-0 sm:pb-0" style={{
    backgroundColor: 'rgba(0,0,0,0.72)'
  }} onClick={e => {
    if (e.target === e.currentTarget) onDecline();
  }} data-id="element-2">
      <motion.div initial={{
      opacity: 0,
      scale: 0.96,
      y: 40
    }} animate={{
      opacity: 1,
      scale: 1,
      y: 0
    }} exit={{
      opacity: 0,
      scale: 0.96,
      y: 40
    }} transition={{
      duration: 0.3,
      type: 'spring',
      stiffness: 280,
      damping: 24
    }} className="relative w-full max-w-md rounded-t-2xl sm:rounded-2xl overflow-hidden flex flex-col" style={{
      backgroundColor: 'var(--cream)',
      border: '2px solid var(--gold)',
      boxShadow: '0 24px 80px rgba(0,0,0,0.45)',
      maxHeight: '92dvh'
    }} data-id="element-3">
        {/* Close */}
        <button onClick={onDecline} className="absolute top-3 right-3 z-10 p-1.5 rounded-full opacity-40 hover:opacity-70 transition-opacity" style={{
        backgroundColor: 'var(--navy)',
        color: 'white'
      }} aria-label="Cerrar" data-id="element-4">
          <X className="w-3.5 h-3.5" data-id="element-5" />
        </button>

        {/* Header banner — sticky */}
        <div className="flex-shrink-0 px-5 py-4 text-center" style={{
        backgroundColor: 'var(--navy)'
      }} data-id="element-6">
          <span className="flash-badge inline-block px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wider mb-3" style={{
          backgroundColor: 'var(--gold)',
          color: 'var(--navy)'
        }} data-id="element-7">
            ⚡ Oferta exclusiva — Solo en esta pantalla
          </span>
          <h2 className="font-serif text-lg sm:text-xl md:text-2xl font-bold leading-tight" style={{
          color: 'white'
        }} data-id="element-8">
            ⛔ ¡Esperá! Estás dejando dinero sobre la mesa
          </h2>
          <p className="text-xs sm:text-sm mt-2" style={{
          color: 'rgba(255,255,255,0.65)'
        }} data-id="element-9">
            Agregá las 2 mejoras por 30% menos — solo si comprás ahora
          </p>
        </div>

        {/* Scrollable body */}
        <div className="flex-1 overflow-y-auto overscroll-contain px-4 sm:px-5 py-4 sm:py-5" data-id="element-10">
          {/* Flashing discount badge */}
          <div className="text-center mb-3" data-id="element-11">
            <span className="flash-badge inline-block px-3 sm:px-4 py-1.5 sm:py-2 rounded-xl text-xs sm:text-sm font-bold" style={{
            backgroundColor: 'var(--green)',
            color: 'white'
          }} data-id="element-12">
              🔥 30% DE DESCUENTO EN LAS 2 MEJORAS — SOLO AHORA
            </span>
          </div>

          {/* Copy */}
          <p className="text-xs sm:text-sm leading-relaxed mb-2" style={{
          color: 'var(--charcoal)'
        }} data-id="element-13">
            Ya tomaste la decisión más importante: empezar tu reset. Pero sin el
            Filtro Anti-Impulso y el Escudo Anti-Cajeros, vas a seguir perdiendo
            dinero por los mismos agujeros que el sistema principal no puede
            tapar solo.
          </p>
          <p className="text-xs sm:text-sm leading-relaxed mb-4" style={{
          color: 'var(--charcoal)',
          opacity: 0.8
        }} data-id="element-14">
            Este bundle te da el sistema completo — el reset, la protección
            contra Amazon y el escudo contra cajeros — todo por $18.99 en lugar
            de $24.39.
          </p>

          {/* What's included */}
          <div className="rounded-xl p-3 sm:p-4 mb-4" style={{
          backgroundColor: 'var(--light-navy)',
          border: '1px solid rgba(200,164,92,0.2)'
        }} data-id="element-15">
            <p className="text-xs font-bold uppercase tracking-wider mb-2.5" style={{
            color: 'var(--navy)'
          }} data-id="element-16">
              Lo que recibís en el bundle completo:
            </p>
            <ul className="space-y-1.5" data-id="element-17">
              {[{
              label: 'Reset de Deudas en 14 Días (USA)',
              sub: 'Sistema principal + 3 bonos gratis'
            }, {
              label: 'Bono #1 — Reset de Suscripciones en 30 Min',
              sub: 'Recuperá dinero de fugas invisibles'
            }, {
              label: 'Bono #2 — Lista Antitento: 50 Reglas',
              sub: 'Frenate antes de gastar de impulso'
            }, {
              label: 'Bono #3 — Mini Kit Pago Extra por Paycheck',
              sub: 'Ejecutá pagos extra cada cobro'
            }, {
              label: '+ Filtro Anti-Impulso Amazon',
              sub: 'Desarmá las trampas del One-Click'
            }, {
              label: '+ Escudo Anti-Cajeros',
              sub: 'Scripts para decir NO al crédito fácil'
            }].map(item => <li key={item.label} className="flex items-start gap-2" data-id="element-18">
                  <CheckIcon className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" style={{
                color: 'var(--green)'
              }} data-id="element-19" />
                  <div data-id="element-20">
                    <span className="text-xs font-semibold" style={{
                  color: 'var(--navy)'
                }} data-id="element-21">
                      {item.label}
                    </span>
                    <span className="text-xs block" style={{
                  color: 'var(--charcoal)',
                  opacity: 0.6
                }} data-id="element-22">
                      {item.sub}
                    </span>
                  </div>
                </li>)}
            </ul>
          </div>

          {/* Pricing block */}
          <div className="rounded-xl p-3 sm:p-4 mb-4 text-center" style={{
          backgroundColor: 'var(--navy)',
          border: '1px solid rgba(200,164,92,0.3)'
        }} data-id="element-23">
            <p className="text-xs mb-1.5" style={{
            color: 'rgba(255,255,255,0.5)'
          }} data-id="element-24">
              Valor real del bundle completo
            </p>
            <div className="flex items-baseline justify-center gap-3 mb-1" data-id="element-25">
              <span className="text-base sm:text-lg line-through" style={{
              color: 'rgba(255,255,255,0.35)'
            }} data-id="element-26">
                ${TRUE_TOTAL}
              </span>
              <span className="font-serif text-3xl sm:text-4xl font-bold" style={{
              color: 'var(--gold)'
            }} data-id="element-27">
                ${BUNDLE_PRICE}
              </span>
            </div>
            <p className="text-xs font-bold uppercase tracking-wider mb-2.5" style={{
            color: 'var(--green)'
          }} data-id="element-28">
              Ahorrás $7.32 — 30% off en el bundle completo
            </p>
            <div className="flex items-center justify-center gap-1.5" data-id="element-29">
              <Lock className="w-3 h-3" style={{
              color: 'rgba(255,255,255,0.4)'
            }} data-id="element-30" />
              <span className="text-xs" style={{
              color: 'rgba(255,255,255,0.4)'
            }} data-id="element-31">
                Precio especial solo disponible en esta pantalla
              </span>
            </div>
          </div>

          {/* Accept CTA */}
          <motion.button onClick={onAccept} whileHover={{
          scale: 1.02
        }} whileTap={{
          scale: 0.97
        }} className="cta-glow w-full py-3.5 sm:py-4 rounded-xl font-bold text-sm sm:text-base tracking-wide mb-2.5" style={{
          backgroundColor: 'var(--navy)',
          color: 'var(--gold)',
          border: '2px solid var(--gold)'
        }} data-id="element-32">
            ✅ SÍ, QUIERO EL BUNDLE COMPLETO — $17.07
          </motion.button>

          {/* Decline */}
          <button onClick={onDecline} className="w-full py-2.5 text-xs sm:text-sm text-center rounded-xl transition-opacity hover:opacity-70 mb-2" style={{
          color: 'var(--charcoal)',
          opacity: 0.5
        }} data-id="element-33">
            No gracias, continuar sin las mejoras →
          </button>
        </div>
      </motion.div>
    </motion.div>;
}
// ─────────────────────────────────────────────────────────────────────────────
interface OrderBumpProps {
  id: string;
  image: string;
  title: string;
  description: string;
  price: string;
  checked: boolean;
  onChange: () => void;
}
function OrderBump({
  id,
  image,
  title,
  description,
  price,
  checked,
  onChange
}: OrderBumpProps) {
  return <div className="rounded-xl overflow-hidden bump-flash" style={{
    border: checked ? '2px solid var(--gold)' : '2px dashed rgba(200, 164, 92, 0.45)',
    backgroundColor: checked ? 'rgba(200, 164, 92, 0.07)' : 'white',
    transition: 'border-color 0.25s, background-color 0.25s, box-shadow 0.25s',
    boxShadow: checked ? '0 0 16px rgba(200, 164, 92, 0.25)' : 'none'
  }} data-id="element-34">
      {/* Header stripe */}
      <div className="px-4 py-2 flex items-center gap-2" style={{
      backgroundColor: 'var(--navy)'
    }} data-id="element-35">
        <PlusCircle className="w-4 h-4 flex-shrink-0" style={{
        color: 'var(--gold)'
      }} data-id="element-36" />
        <span className="text-xs font-bold uppercase tracking-wider" style={{
        color: 'var(--gold)'
      }} data-id="element-37">
          Mejora especial — Agregar a mi orden
        </span>
      </div>

      <label htmlFor={id} className="flex gap-4 p-4 cursor-pointer" data-id="element-38">
        <img src={image} alt={title} className="w-20 h-20 rounded-lg object-cover flex-shrink-0 shadow-sm" data-id="element-39" />
        <div className="flex-1 min-w-0" data-id="element-40">
          <p className="font-bold text-sm leading-snug mb-2" style={{
          color: 'var(--navy)'
        }} data-id="element-41">
            {title}
          </p>
          <p className="text-xs leading-relaxed mb-3" style={{
          color: 'var(--charcoal)',
          opacity: 0.75
        }} data-id="element-42">
            {description}
          </p>
          <div className="flex items-center gap-3" data-id="element-43">
            <input id={id} type="checkbox" checked={checked} onChange={onChange} className="w-4 h-4 accent-amber-600 cursor-pointer flex-shrink-0" data-id="element-44" />
            <span className="text-sm font-bold" style={{
            color: checked ? 'var(--gold)' : 'var(--navy)'
          }} data-id="element-45">
              {checked ? `Agregado — ${price}` : `Sí, agregar por solo ${price}`}
            </span>
          </div>
        </div>
      </label>
    </div>;
}
interface BonusCTAProps {
  variant?: 'mini' | 'full';
}
export function BonusCTA({
  variant = 'full'
}: BonusCTAProps) {
  const [ob1, setOb1] = useState(false);
  const [ob2, setOb2] = useState(false);
  const [showUpsell, setShowUpsell] = useState(false);
  const BASE_PRICE = 6.39;
  const BUMP_PRICE = 9;
  const totalPrice = BASE_PRICE + (ob1 ? BUMP_PRICE : 0) + (ob2 ? BUMP_PRICE : 0);
  const totalOriginal = 160.99 + (ob1 ? 27 : 0) + (ob2 ? 27 : 0);
  const formattedTotal = totalPrice.toFixed(2);
  // ─── Shopify cart URLs por combinación ───────────────────────────────────
  const CART_MAIN = 'https://mdbhorizonte.com/cart/52013919502628:1';
  const CART_OB1_ONLY = 'https://mdbhorizonte.com/cart/52032531857700:1';
  const CART_OB2_ONLY = 'https://mdbhorizonte.com/cart/52032532939044:1';
  const CART_OB1_OB2 = 'https://mdbhorizonte.com/cart/52032551878948:1'; // ambos bumps → $24.39
  const CART_BUNDLE_DISCOUNT = 'https://mdbhorizonte.com/cart/52033541210404:1'; // bundle con 30% off → $17.07 (solo desde popup upsell)
  const getCartUrl = () => {
    if (ob1 && ob2) return CART_OB1_OB2;
    if (ob1) return CART_OB1_ONLY;
    if (ob2) return CART_OB2_ONLY;
    return CART_MAIN;
  };
  const handleClick = (e: React.MouseEvent) => {
    // If no bumps selected, intercept and show upsell popup
    if (!ob1 && !ob2) {
      e.preventDefault();
      setShowUpsell(true);
      return;
    }
    if (typeof window !== 'undefined' && window.fbq) {
      window.fbq('track', 'AddToCart', {
        content_name: 'Reset de Deudas en 14 Días (USA)',
        content_type: 'product',
        value: totalPrice,
        currency: 'USD'
      });
    }
  };
  const handleUpsellAccept = () => {
    setShowUpsell(false);
    if (typeof window !== 'undefined' && window.fbq) {
      window.fbq('track', 'AddToCart', {
        content_name: 'Bundle Completo',
        content_type: 'product',
        value: 17.07,
        currency: 'USD'
      });
    }
    window.open(CART_BUNDLE_DISCOUNT, '_blank');
  };
  const handleUpsellDecline = () => {
    setShowUpsell(false);
    if (typeof window !== 'undefined' && window.fbq) {
      window.fbq('track', 'AddToCart', {
        content_name: 'Reset de Deudas en 14 Días (USA)',
        content_type: 'product',
        value: BASE_PRICE,
        currency: 'USD'
      });
    }
    window.open(CART_MAIN, '_blank');
  };
  if (variant === 'mini') {
    return <motion.div initial={{
      opacity: 0,
      y: 12
    }} whileInView={{
      opacity: 1,
      y: 0
    }} viewport={{
      once: true
    }} transition={{
      duration: 0.5
    }} className="my-10 text-center" data-id="element-46">
        <p className="text-sm mb-4" style={{
        color: 'var(--charcoal)',
        opacity: 0.6
      }} data-id="element-47">
          Incluye este bono + 2 más al comprar hoy
        </p>
        <motion.a href="#bonus-cta-final" whileHover={{
        scale: 1.02
      }} whileTap={{
        scale: 0.98
      }} className="inline-block px-8 py-4 rounded-xl font-bold text-base tracking-wide" style={{
        backgroundColor: 'var(--navy)',
        color: 'var(--gold)',
        border: '2px solid var(--gold)'
      }} data-id="element-48">
          QUIERO MI RESET + LOS 3 BONOS
        </motion.a>
        <p className="text-xs mt-3" style={{
        color: 'var(--charcoal)',
        opacity: 0.45
      }} data-id="element-49">
          Acceso inmediato — $6.39 hoy (bonos incluidos gratis)
        </p>
      </motion.div>;
  }
  return <section id="bonus-cta-final" className="w-full py-20 md:py-28 px-6" style={{
    backgroundColor: 'var(--cream)'
  }} data-id="element-50">
      <div className="max-w-2xl mx-auto text-center" data-id="element-51">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.7
      }} data-id="element-52">
          {/* Value summary */}
          <div className="rounded-2xl p-6 mb-10 text-left" style={{
          backgroundColor: 'var(--light-navy)',
          border: '1px solid rgba(200, 164, 92, 0.25)'
        }} data-id="element-53">
            <div className="flex items-center gap-2 mb-5" data-id="element-54">
              <GiftIcon className="w-5 h-5" style={{
              color: 'var(--gold)'
            }} data-id="element-55" />
              <p className="font-bold text-base" style={{
              color: 'var(--navy)'
            }} data-id="element-56">
                Todo lo que recibís hoy:
              </p>
            </div>
            <ul className="space-y-3 mb-5" data-id="element-57">
              {[{
              label: 'Reset de Deudas en 14 Días (USA)',
              value: '$19.99',
              price: '$6.39',
              main: true
            }, {
              label: 'Bono #1 — Reset de Suscripciones en 30 Min',
              value: '$47',
              price: 'GRATIS'
            }, {
              label: 'Bono #2 — Lista Antitento: 50 Reglas',
              value: '$47',
              price: 'GRATIS'
            }, {
              label: 'Bono #3 — Mini Kit Pago Extra por Paycheck',
              value: '$47',
              price: 'GRATIS'
            }].map(item => <li key={item.label} className="flex items-center justify-between gap-4" data-id="element-58">
                  <span className={`text-sm ${item.main ? 'font-semibold' : ''}`} style={{
                color: 'var(--navy)'
              }} data-id="element-59">
                    {item.label}
                  </span>
                  <div className="flex items-center gap-2 flex-shrink-0" data-id="element-60">
                    <span className="text-sm line-through" style={{
                  color: 'var(--charcoal)',
                  opacity: 0.4
                }} data-id="element-61">
                      {item.value}
                    </span>
                    <span className="text-sm font-bold" style={{
                  color: item.main ? 'var(--green)' : 'var(--gold)'
                }} data-id="element-62">
                      {item.price}
                    </span>
                  </div>
                </li>)}
              {/* Dynamic bump rows */}
              {ob1 && <li className="flex items-center justify-between gap-4" data-id="element-63">
                  <span className="text-sm" style={{
                color: 'var(--navy)'
              }} data-id="element-64">
                    + Filtro Anti-Impulso Amazon
                  </span>
                  <div className="flex items-center gap-2 flex-shrink-0" data-id="element-65">
                    <span className="text-sm font-bold" style={{
                  color: 'var(--gold)'
                }} data-id="element-66">
                      $9
                    </span>
                  </div>
                </li>}
              {ob2 && <li className="flex items-center justify-between gap-4" data-id="element-67">
                  <span className="text-sm" style={{
                color: 'var(--navy)'
              }} data-id="element-68">
                    + Escudo Anti-Cajeros
                  </span>
                  <div className="flex items-center gap-2 flex-shrink-0" data-id="element-69">
                    <span className="text-sm font-bold" style={{
                  color: 'var(--gold)'
                }} data-id="element-70">
                      $9
                    </span>
                  </div>
                </li>}
            </ul>
            <div className="flex items-center justify-between pt-4" style={{
            borderTop: '1px solid rgba(27, 42, 74, 0.1)'
          }} data-id="element-71">
              <span className="font-bold" style={{
              color: 'var(--navy)'
            }} data-id="element-72">
                Total hoy
              </span>
              <div className="flex items-baseline gap-3" data-id="element-73">
                <span className="line-through text-sm" style={{
                color: 'var(--charcoal)',
                opacity: 0.4
              }} data-id="element-74">
                  ${totalOriginal.toFixed(2)}
                </span>
                <AnimatePresence mode="wait" data-id="element-75">
                  <motion.span key={formattedTotal} initial={{
                  opacity: 0,
                  y: -6
                }} animate={{
                  opacity: 1,
                  y: 0
                }} exit={{
                  opacity: 0,
                  y: 6
                }} transition={{
                  duration: 0.2
                }} className="font-bold text-xl" style={{
                  color: 'var(--green)'
                }} data-id="element-76">
                    ${formattedTotal}
                  </motion.span>
                </AnimatePresence>
              </div>
            </div>
          </div>

          {/* Headline */}
          <p className="font-serif text-xl md:text-2xl leading-relaxed mb-3" style={{
          color: 'var(--charcoal)',
          lineHeight: '1.6'
        }} data-id="element-77">
            Todo el sistema. Los 3 bonos. Acceso inmediato.
          </p>
          <AnimatePresence mode="wait" data-id="element-78">
            <motion.p key={formattedTotal} initial={{
            opacity: 0,
            scale: 0.97
          }} animate={{
            opacity: 1,
            scale: 1
          }} exit={{
            opacity: 0,
            scale: 0.97
          }} transition={{
            duration: 0.2
          }} className="font-serif text-2xl md:text-3xl font-semibold mb-10" style={{
            color: 'var(--navy)'
          }} data-id="element-79">
              Por ${formattedTotal}. Los bonos: gratis.
            </motion.p>
          </AnimatePresence>

          {/* Price badge */}
          <div className="flex flex-col items-center gap-2 mb-8" data-id="element-80">
            <AnimatePresence mode="wait" data-id="element-81">
              <motion.span key={formattedTotal} initial={{
              opacity: 0,
              y: -4
            }} animate={{
              opacity: 1,
              y: 0
            }} exit={{
              opacity: 0,
              y: 4
            }} transition={{
              duration: 0.2
            }} className="inline-block px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wider" style={{
              backgroundColor: 'var(--green)',
              color: 'white'
            }} data-id="element-82">
                Valor total ${totalOriginal.toFixed(2)} — Pagás solo $
                {formattedTotal}
              </motion.span>
            </AnimatePresence>
            <div className="flex items-baseline gap-3" data-id="element-83">
              <span className="text-xl line-through" style={{
              color: 'var(--charcoal)',
              opacity: 0.4
            }} data-id="element-84">
                ${totalOriginal.toFixed(2)}
              </span>
              <AnimatePresence mode="wait" data-id="element-85">
                <motion.span key={formattedTotal} initial={{
                opacity: 0,
                scale: 0.85
              }} animate={{
                opacity: 1,
                scale: 1
              }} exit={{
                opacity: 0,
                scale: 0.85
              }} transition={{
                duration: 0.25,
                type: 'spring',
                stiffness: 300
              }} className="font-serif text-5xl font-bold" style={{
                color: 'var(--green)'
              }} data-id="element-86">
                  ${formattedTotal}
                </motion.span>
              </AnimatePresence>
            </div>
            <p className="text-sm" style={{
            color: 'var(--charcoal)',
            opacity: 0.55
          }} data-id="element-87">
              Los 3 bonos ($141 en valor) incluidos completamente gratis
            </p>
          </div>

          {/* CTA Button */}
          <motion.a href={getCartUrl()} target="_blank" rel="noopener noreferrer" onClick={handleClick} whileHover={{
          scale: 1.02
        }} whileTap={{
          scale: 0.98
        }} className="cta-glow inline-block px-10 py-5 rounded-xl text-lg font-bold tracking-wide" style={{
          backgroundColor: 'var(--navy)',
          color: 'var(--gold)',
          border: '2px solid var(--gold)'
        }} aria-label="Comprar Reset de Deudas en 14 Días con los 3 bonos" data-id="element-88">
            QUIERO MI RESET + LOS 3 BONOS GRATIS
          </motion.a>

          {/* Order Bumps */}
          <div className="mt-8 space-y-4 text-left" data-id="element-89">
            <OrderBump id="ob1" image="https://cdn.magicpatterns.com/uploads/kaMUkMJLu6j8wGYV1TUHj7/ORDER_BUMP_1.png" title="¡ALTO! No dejes que Amazon se robe tu progreso (Filtro Anti-Impulso)" description="¿Sabías que Amazon gasta millones en ingeniería psicológica para que hagas clic en 'Comprar ahora' sin pensar? Agregá La Lista Negra de Amazon a tu orden. Vas a descubrir cómo desarmar las trampas del 'One-Click', desactivar los anuncios que te persiguen y aprender el truco de los 10 segundos para ignorar las 'Ofertas Relámpago' que drenan tu cuenta. Evitá gastos innecesarios de cientos de dólares por solo $9 hoy." price="$9" checked={ob1} onChange={() => setOb1(!ob1)} data-id="element-90" />
            <OrderBump id="ob2" image="https://cdn.magicpatterns.com/uploads/qn62UW5gxw4TAPAuq4Sx8i/ORDER_BUMP_2.png" title="Escudo Anti-Cajeros: Cómo decir NO al crédito fácil en la caja" description="Estás en el checkout de Target o Walmart y el cajero te ofrece un '10% de descuento hoy' si abrís su tarjeta. Parece un ahorro, pero es una trampa de intereses del 32%. Llevate los Scripts de Emergencia: guiones exactos y cortos para rechazar estas ofertas con firmeza y sin sentirte mal. Protegé tu Score de Crédito de aplicaciones innecesarias y ahorrate miles en intereses. Agregalo por solo $9 y nunca más caigas en la trampa del 'ahorro' falso." price="$9" checked={ob2} onChange={() => setOb2(!ob2)} data-id="element-91" />
          </div>

          {/* Post-bump order summary + final buy button */}
          <AnimatePresence data-id="element-92">
            <motion.div key={`${ob1}-${ob2}`} initial={{
            opacity: 0,
            y: 8
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.3
          }} className="mt-6 rounded-2xl p-5 text-left" style={{
            backgroundColor: 'var(--navy)',
            border: '1px solid rgba(200, 164, 92, 0.3)'
          }} data-id="element-93">
              <p className="text-xs font-bold uppercase tracking-wider mb-4" style={{
              color: 'var(--gold)'
            }} data-id="element-94">
                Tu orden final:
              </p>
              <ul className="space-y-2 mb-4" data-id="element-95">
                <li className="flex items-center justify-between gap-4" data-id="element-96">
                  <span className="text-sm font-medium" style={{
                  color: 'white'
                }} data-id="element-97">
                    Reset de Deudas en 14 Días (USA) + 3 Bonos
                  </span>
                  <span className="text-sm font-bold flex-shrink-0" style={{
                  color: 'var(--gold)'
                }} data-id="element-98">
                    $6.39
                  </span>
                </li>
                {ob1 && <motion.li initial={{
                opacity: 0,
                x: -8
              }} animate={{
                opacity: 1,
                x: 0
              }} className="flex items-center justify-between gap-4" data-id="element-99">
                    <span className="text-sm" style={{
                  color: 'rgba(255,255,255,0.75)'
                }} data-id="element-100">
                      + Filtro Anti-Impulso Amazon
                    </span>
                    <span className="text-sm font-bold flex-shrink-0" style={{
                  color: 'var(--gold)'
                }} data-id="element-101">
                      $9
                    </span>
                  </motion.li>}
                {ob2 && <motion.li initial={{
                opacity: 0,
                x: -8
              }} animate={{
                opacity: 1,
                x: 0
              }} className="flex items-center justify-between gap-4" data-id="element-102">
                    <span className="text-sm" style={{
                  color: 'rgba(255,255,255,0.75)'
                }} data-id="element-103">
                      + Escudo Anti-Cajeros
                    </span>
                    <span className="text-sm font-bold flex-shrink-0" style={{
                  color: 'var(--gold)'
                }} data-id="element-104">
                      $9
                    </span>
                  </motion.li>}
              </ul>
              <div className="flex items-center justify-between pt-3 mb-5" style={{
              borderTop: '1px solid rgba(200, 164, 92, 0.2)'
            }} data-id="element-105">
                <span className="font-bold text-base" style={{
                color: 'white'
              }} data-id="element-106">
                  Total
                </span>
                <AnimatePresence mode="wait" data-id="element-107">
                  <motion.span key={formattedTotal} initial={{
                  opacity: 0,
                  scale: 0.85
                }} animate={{
                  opacity: 1,
                  scale: 1
                }} exit={{
                  opacity: 0,
                  scale: 0.85
                }} transition={{
                  duration: 0.2,
                  type: 'spring',
                  stiffness: 300
                }} className="font-serif text-2xl font-bold" style={{
                  color: 'var(--gold)'
                }} data-id="element-108">
                    ${formattedTotal}
                  </motion.span>
                </AnimatePresence>
              </div>

              {/* Final buy button */}
              <motion.a href={getCartUrl()} target="_blank" rel="noopener noreferrer" onClick={e => {
              if (typeof window !== 'undefined' && window.fbq) {
                window.fbq('track', 'AddToCart', {
                  content_name: 'Reset de Deudas en 14 Días (USA)',
                  content_type: 'product',
                  value: totalPrice,
                  currency: 'USD'
                });
              }
            }} whileHover={{
              scale: 1.02
            }} whileTap={{
              scale: 0.98
            }} className="cta-glow block w-full py-4 rounded-xl text-base font-bold tracking-wide text-center" style={{
              backgroundColor: 'var(--gold)',
              color: 'var(--navy)'
            }} aria-label="Completar compra" data-id="element-109">
                COMPLETAR MI ORDEN — ${formattedTotal}
              </motion.a>
              <p className="text-xs text-center mt-3" style={{
              color: 'rgba(255,255,255,0.4)'
            }} data-id="element-110">
                Acceso inmediato al pago · Pago seguro
              </p>
            </motion.div>
          </AnimatePresence>

          {/* Microcopy */}
          <div className="mt-8 flex flex-wrap items-center justify-center gap-4" data-id="element-111">
            {['Acceso inmediato (PDF)', 'Hecho para 35+ en USA', 'Sin apps complicadas', '3 bonos incluidos'].map(item => <span key={item} className="flex items-center gap-1.5 text-sm" style={{
            color: 'var(--charcoal)',
            opacity: 0.6
          }} data-id="element-112">
                <CheckIcon className="w-4 h-4" style={{
              color: 'var(--green)'
            }} data-id="element-113" />
                {item}
              </span>)}
          </div>
        </motion.div>
      </div>

      {/* Exit upsell popup — rendered via portal to avoid stacking context issues */}
      {typeof document !== 'undefined' && createPortal(<AnimatePresence data-id="element-114">
            {showUpsell && <UpsellPopup onAccept={handleUpsellAccept} onDecline={handleUpsellDecline} data-id="element-115" />}
          </AnimatePresence>, document.body)}
    </section>;
}